import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import Colours from '@shared/ui/atoms/Colours';

interface Props {
  trigger: ReactNode;
  popup: ReactNode;
  delayMs?: number;
  onOpen?: () => void;
  className?: string;
  style?: React.CSSProperties;
  controlledOpen?: boolean;
  // Which edge of the popup lines up with the matching edge of the trigger.
  align?: 'left' | 'right';
}

export function HoverPopup({ trigger, popup, delayMs = 0, onOpen, className, style, controlledOpen, align = 'right' }: Props) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (next: boolean) => {
    if (!isControlled) setInternalOpen(next);
  };
  const [boxPos, setBoxPos] = useState({ top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const openTimerRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  const updatePosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setBoxPos({ top: rect.bottom + 4, left: align === 'left' ? rect.left : rect.right });
  }, [align]);

  const scheduleUpdate = useCallback(() => {
    if (rafRef.current !== null) return;
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null;
      updatePosition();
    });
  }, [updatePosition]);

  useEffect(() => {
    if (!open) return;
    updatePosition();
    onOpen?.();
    window.addEventListener('scroll', scheduleUpdate, true);
    window.addEventListener('resize', scheduleUpdate);
    return () => {
      window.removeEventListener('scroll', scheduleUpdate, true);
      window.removeEventListener('resize', scheduleUpdate);
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [open, updatePosition, scheduleUpdate, onOpen]);

  const handleEnter = () => {
    if (delayMs <= 0) {
      setOpen(true);
      return;
    }
    openTimerRef.current = window.setTimeout(() => setOpen(true), delayMs);
  };

  const handleLeave = () => {
    if (openTimerRef.current !== null) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    setOpen(false);
  };

  return (
    <Container ref={containerRef} onMouseEnter={handleEnter} onMouseLeave={handleLeave} className={className} style={style}>
      {trigger}
      {open && ReactDOM.createPortal(
        <HoverBox $align={align} style={{ top: boxPos.top, left: boxPos.left }}>{popup}</HoverBox>,
        document.body,
      )}
    </Container>
  );
}

const Container = styled.div`
  display: inline-flex;
`;

const HoverBox = styled.div<{ $align: 'left' | 'right' }>`
  position: fixed;
  z-index: 999999;
  min-width: 200px;
  min-height: 40px;
  background-color: white;
  transform: ${props => props.$align === 'left' ? 'none' : 'translateX(-100%)'};
  border-radius: 8px;
  border: 1px solid ${Colours.veryLightGrey};
  box-shadow: 0 0 17px 2px rgba(0, 87, 255, 0.15);
  padding: 4px 8px;
`;
